/**
 * AutopilotRecommendations — Konkrete Empfehlungen für den Monatsplan
 *
 * Kombiniert zwei Quellen:
 *   1. Analytics der letzten Wochen (was performt gut)
 *   2. Aktueller Monatsplan (was fehlt, was ist übermäßig vertreten)
 *
 * Design-Entscheidungen:
 * - Vollständig unsichtbar wenn leer (kein leerer Container)
 * - Jede Karte: kurze Headline + 1 Satz + 1 klarer CTA
 * - CTAs docken an bestehende Flows an: Autopilot-Wizard, Video, Content Tools
 * - Kein Fachjargon: keine Prozentzahlen, keine internen Typnamen
 * - Karte-Farben signalisieren Handlungsdringlichkeit, nicht Alarm
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lightbulb, ArrowRight, Zap } from "lucide-react";
import useIsMaster from "../hooks/useIsMaster";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Typ → visuelle Eigenschaften ─────────────────────────────────────────────

const TYPE_STYLE = {
  fill_gap:            "border-amber-200 bg-amber-50",
  rebalance_offer:     "border-amber-200 bg-amber-50",
  double_down_format:  "border-green-200 bg-green-50",
  double_down_pillar:  "border-green-200 bg-green-50",
  revive_top_performer:"border-blue-200 bg-blue-50",
  video_opportunity:   "border-violet-200 bg-violet-50",
};

const CTA_STYLE = {
  fill_gap:            "bg-amber-600 hover:bg-amber-700 text-white",
  rebalance_offer:     "bg-amber-600 hover:bg-amber-700 text-white",
  double_down_format:  "bg-green-600 hover:bg-green-700 text-white",
  double_down_pillar:  "bg-green-600 hover:bg-green-700 text-white",
  revive_top_performer:"bg-blue-600 hover:bg-blue-700 text-white",
  video_opportunity:   "bg-violet-600 hover:bg-violet-700 text-white",
};

// ── Einzel-Karte ──────────────────────────────────────────────────────────────

const RecommendationCard = ({ rec, onOpenAutopilot }) => {
  const navigate = useNavigate();

  const handleCta = () => {
    const routes = {
      open_autopilot:      null,
      open_content_tools:  "/content-tools",
      open_video:          "/video",
      open_evergreen:      "/evergreen",
    };
    const route = routes[rec.cta_action];
    if (route) {
      navigate(route);
    } else {
      // "open_autopilot" → sanftes Scrollen zum Wizard auf derselben Seite
      onOpenAutopilot?.();
    }
  };

  const cardStyle = TYPE_STYLE[rec.type] || "border-stone-200 bg-stone-50";
  const ctaStyle  = CTA_STYLE[rec.type]  || "bg-stone-600 hover:bg-stone-700 text-white";

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${cardStyle}`}>
      <div>
        <p className="font-semibold text-stone-800 text-sm leading-snug">
          {rec.title}
        </p>
        <p className="text-stone-600 text-sm mt-1 leading-relaxed">
          {rec.message}
        </p>
      </div>
      <button
        onClick={handleCta}
        className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${ctaStyle}`}
      >
        {rec.cta_label}
        <ArrowRight size={12} />
      </button>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const AutopilotRecommendations = ({
  platforms = ["instagram", "facebook"],
  month,
  year,
  onOpenAutopilot,
}) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const isMaster              = useIsMaster();

  useEffect(() => {
    if (!month || !year) return;
    const platformStr = platforms.join(",");
    axios
      .get(`${API}/autopilot/recommendations?platforms=${platformStr}&month=${month}&year=${year}`)
      .then((res) => setData(res.data))
      .catch(() => { /* Silently ignore — non-critical */ })
      .finally(() => setLoading(false));
  }, [month, year, platforms.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Video-Empfehlungen sind ein Inhaber-Thema: Kunden haben keinen Video-Weg,
  // ein CTA dorthin wäre ein leeres Versprechen (und liefe ins 403). Das Backend
  // erzeugt die Empfehlung weiterhin — sie wird hier für Kunden herausgefiltert.
  const recommendations = (data?.recommendations || []).filter(
    (rec) => isMaster || rec.cta_action !== "open_video"
  );

  // Komplett unsichtbar wenn lädt oder keine Empfehlungen
  if (loading || !recommendations.length) return null;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Lightbulb size={15} className="text-fox-fire flex-shrink-0" />
        <p className="text-sm font-semibold text-stone-700">
          Empfehlungen für deinen Plan
        </p>
        <span className="text-xs text-stone-400 font-normal">
          · {recommendations.length} {recommendations.length === 1 ? "Hinweis" : "Hinweise"}
        </span>
      </div>

      {/* Karten */}
      <div className="grid gap-2.5 sm:grid-cols-2">
        {recommendations.map((rec, i) => (
          <RecommendationCard
            key={i}
            rec={rec}
            onOpenAutopilot={onOpenAutopilot}
          />
        ))}
      </div>
    </div>
  );
};

export default AutopilotRecommendations;
